import { TiVoTargets } from "hooks/tivoApps";
import { FieldProps } from "types/form";
import { unique } from "utils/array";

import { TiVoTargetsForm, TiVoTargetsListProps } from "./types";
import { TiVoDeviceSource, useTiVoDeviceData } from "./useTiVoDeviceData";

export function adaptReturnValue(
  input: TiVoTargetsForm,
  tivoDeviceData: TiVoDeviceSource
): TiVoTargets {
  let oems = unique(input.oems, input.clientGroups);
  let platforms = unique(input.platforms, input.oemGroups);

  // 1 Add missing parents with children
  const allClientTypes = tivoDeviceData.getClientTypes();
  const allOems = tivoDeviceData.getOems();
  const requiredOems = Object.keys(allClientTypes).filter((o) =>
    allClientTypes[o].some((c) => input.clientTypes?.includes(c))
  );
  const requiredPlatforms = Object.keys(allOems).filter((p) =>
    allOems[p].some((o) => oems?.includes(o) || requiredOems.includes(o))
  );
  oems = unique(oems, requiredOems);
  platforms = unique(platforms, requiredPlatforms);

  // 2 Remove parent without children
  oems = oems.filter((o) => {
    const clientTypeIds = tivoDeviceData.getClientTypeIds(platforms, [o]);
    // remove oems if no client is selected, but only if oem has clients
    return (
      clientTypeIds.length === 0 ||
      clientTypeIds.some((c) => input.clientTypes.includes(c))
    );
  });
  platforms = platforms.filter((p) =>
    oems.some((o) => tivoDeviceData.getOemIds([p]).includes(o))
  );

  // 3 Remove items for all selected groups
  const clients = tivoDeviceData.getClientTypes(platforms, oems);
  const clientsToRemove = input.clientGroups.flatMap((g) => clients[g] || []);
  const tempOems = tivoDeviceData.getOems(platforms);
  const oemsToRemove = input.oemGroups.flatMap((g) => tempOems[g] || []);

  return {
    platforms,
    oems: oems.filter((o) => !oemsToRemove.includes(o)),
    clientTypes: input.clientTypes.filter((c) => !clientsToRemove.includes(c)),
  };
}

/**
 If group is selected, but has no children, then it means:
 - all children should be selected
 - group should be added to group list (so group switch is on)
 */
export function getInitialValues(
  targets: TiVoTargets,
  tivoDeviceData: TiVoDeviceSource,
  restrictTargets?: TiVoTargets
): TiVoTargetsForm {
  // Check oemGroups
  // if platform & no oems -> all devices from group
  const filteredPlatforms =
    targets.platforms?.filter(
      (p) =>
        !restrictTargets?.platforms?.length ||
        restrictTargets?.platforms?.includes(p)
    ) || [];
  const oemGroups =
    filteredPlatforms.filter((platform) => {
      const allOems = tivoDeviceData.getOemIds([platform], restrictTargets);
      const remaining = allOems.filter((oem) => !targets.oems?.includes(oem));
      return allOems.length === remaining.length;
    }) || [];

  const allGroupOems =
    oemGroups?.flatMap((oemGroup) =>
      tivoDeviceData.getOemIds([oemGroup], restrictTargets)
    ) || [];
  const oems = unique(targets.oems || [], allGroupOems);

  // Check clientGroups
  const clientGroups =
    oems?.filter((oem) => {
      const allClientTypes = tivoDeviceData.getClientTypeIds(
        targets.platforms,
        [oem],
        restrictTargets
      );

      const remaining = allClientTypes.filter(
        (client) => !targets.clientTypes?.includes(client)
      );
      return allClientTypes.length === remaining.length;
    }) || [];

  const allGroupClientTypes =
    clientGroups?.flatMap((clientGroup) =>
      tivoDeviceData.getClientTypeIds(
        targets.platforms,
        [clientGroup],
        restrictTargets
      )
    ) || [];

  return {
    platforms: filteredPlatforms || [],
    oems,
    clientTypes: unique(targets.clientTypes || [], allGroupClientTypes),
    oemGroups,
    clientGroups,
  };
}

// This should return items for selected values, but also restricted by available target restrictions.
// All platforms should be returned unless filtered by platforms
function getFieldOptions(
  tivoDeviceData: TiVoDeviceSource,
  name: string,
  values: TiVoTargets,
  restrictTargets?: TiVoTargets
): Record<string, string[]> {
  switch (name) {
    case "oems": {
      return tivoDeviceData.getOems(undefined, restrictTargets);
    }
    case "clientTypes": {
      return tivoDeviceData.getClientTypes(
        undefined,
        values.oems,
        restrictTargets
      );
    }
  }
  return {};
}

export function getFieldsSetup(
  fields: FieldProps["fields"],
  values: TiVoTargets,
  tivoDeviceData: ReturnType<typeof useTiVoDeviceData>,
  restrictTargets?: TiVoTargets,
  enableSlugBulkSelection = false
): TiVoTargetsListProps[] {
  const cols = fields?.filter((column) => column.name !== "platforms");
  return (
    cols?.map((opt) => {
      const targetFieldKey = opt.name as TiVoTargetsListProps["listName"];
      const targetOptions = getFieldOptions(
        tivoDeviceData,
        targetFieldKey,
        values,
        restrictTargets
      );

      return {
        listName: targetFieldKey,
        label: opt.label, // @todo label should use: group / items
        items: targetOptions,
        restrictTargets,
        enableSlugBulkSelection,
      };
    }) || []
  );
}

export function getSelectedPlatformTypes(
  values: TiVoTargetsForm,
  getPlatformType: (deviceId: string) => string
): string[] {
  return unique(
    [...values.oems, ...values.clientTypes].map((deviceId) =>
      getPlatformType(deviceId)
    )
  );
}
